import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Calendar, XIcon } from '../icon/icon';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { useState } from 'react';
import { InputBtn } from '../items/inputBtn';
import { useRecoilValue } from 'recoil';
import { authState } from '../../atoms/authState';
import { OnOffButton } from '../items/on.off.button';

interface SearchConditionProps {
  handleSearchClose: () => void;
  handleSearchSubmit: (
    startDate: Date,
    endDate: Date,
    name?: string,
    switchStates?: boolean,
  ) => void;
  initialSwitchState?: boolean; // 새로운 prop 정의
  preferredState?: boolean;
}

export const SearchCondition = ({
  handleSearchClose,
  handleSearchSubmit,
  initialSwitchState = false,
  preferredState = false,
}: SearchConditionProps) => {
  const today = new Date();
  const { register, handleSubmit, control } = useForm();
  const [startDate, setStartDate] = useState<Date | null>(today);
  const [endDate, setEndDate] = useState<Date | null>(today);
  const [switchStates, setSwitchStates] = useState<boolean>(initialSwitchState);

  const auth = useRecoilValue(authState);

  const onSubmit = (data: any) => {
    const { searchText } = data; // form에서 검색어 가져오기

    // 초기값 설정
    let startDateToUse = startDate;
    let endDateToUse = endDate;

    // searchText가 존재하는 경우, startDate와 endDate를 특정 범위로 설정
    if (searchText) {
      startDateToUse = new Date('2020-01-01'); // 2020년 1월 1일
      endDateToUse = new Date('2222-12-31'); // 2222년 12월 31일
    }

    // startDateToUse와 endDateToUse가 null이 아닌 경우에만 로그와 함수 호출
    if (startDateToUse && endDateToUse) {
      // 검색 조건을 상위 컴포넌트로 전달
      handleSearchSubmit(
        startDateToUse,
        endDateToUse,
        searchText,
        switchStates,
      );
    } else {
      console.error('Invalid date range');
    }
  };

  const toggleSwitch = () => {
    setSwitchStates((prevState) => !prevState);
  };

  return (
    <Container
      userType={auth.userType ?? 'driver'}
      preferredState={preferredState}
    >
      <motion.div
        className="wrapper"
        initial={{ y: '100%' }}
        animate={{ y: '0%' }}
        exit={{ y: '100%' }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 40,
        }}
      >
        <div className="overlay" onClick={handleSearchClose}></div>
        <div className="wrap-shape">
          <div className="header">
            <span>조회 조건</span>
            <div className="icon-box" onClick={handleSearchClose}>
              <XIcon />
            </div>
          </div>
          <div className="body">
            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <DatePickerContainer>
                  <div className="inner">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={value}
                          startDate={startDate ?? undefined}
                          selectsStart
                          onChange={(date: Date | null) => {
                            setStartDate(date);
                            onChange(date);
                          }}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                  <span>~</span>
                  <div className="inner">
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={value}
                          endDate={endDate ?? undefined}
                          selectsStart
                          onChange={(date: Date | null) => {
                            setEndDate(date);
                            onChange(date);
                          }}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                </DatePickerContainer>
                {auth.userType === 'driver' ? (
                  <>
                    <input
                      type="text"
                      {...register('searchText')}
                      placeholder="검색어를 입력하세요. ex.회사명"
                    />
                    {preferredState ? (
                      <div className="box-item on-off-btn">
                        <div className="title">선호 회사만 보기</div>
                        <OnOffButton
                          isOn={switchStates}
                          toggleSwitch={() => toggleSwitch()}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}

                <InputBtn name="검색" type="submit" />
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </Container>
  );
};

// style
const Container = styled.div<{ userType: string; preferredState?: boolean }>`
  display: flex;
  align-items: flex-end;
  height: 100%;
  position: relative;
  .wrapper {
    width: 100%;
    height: 100%;
    .wrap-shape {
      background: ${(props) => props.theme.textWhiteColor};
      border-radius: 2rem 2rem 0 0;
      overflow: hidden;
      padding: 0.8rem 2.2rem;
      /* height: 23.7rem; */
      height: ${(props) => {
        const preferred = props.preferredState ?? false; // undefined인 경우 기본값으로 false 사용
        console.log('userType:', props.userType);
        console.log('preferredState:', preferred);
        return props.userType === 'driver'
          ? preferred
            ? '32rem'
            : '28rem'
          : '23.7rem';
      }};
      width: 100%;
      position: absolute;
      bottom: 0;
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        span {
          font-size: 1.7rem;
          font-weight: 600;
        }
        .icon-box {
          display: flex;
          transform: translateX(1.6rem);
        }
      }
      .body {
        form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 1.2rem;
          margin-top: 0.9rem;
          input[type='text'] {
            padding: 1.4rem 1.8rem 1.5rem;
            border-radius: 0.8rem;
            border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
            &::placeholder {
              color: ${(props) => props.theme.searchPlaceholderColor};
            }
          }
          .on-off-btn {
            display: flex;
            align-items: center;
            justify-content: space-between;
            .title {
              margin-bottom: 0;
              font-size: 1.4rem;
            }
          }
        }
      }
    }
  }
`;

const CustomDatePicker = styled(DatePicker)``;

export const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  span {
    margin: 0 1rem;
    font-size: 1.4rem;
    font-weight: 400;
    transform: translateY(1px);
    color: ${(props) => props.theme.joinLabelColor};
  }
  .inner {
    position: relative;
    .search-input {
      padding: 1.4rem 5rem 1.5rem 1.75rem;
      max-width: 15rem;
    }
    .react-datepicker {
      font-size: 1.2rem;
      font-family: 'SpoqaHanSansNeo', sans-serif;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 1.25rem;
      transform: translateY(-50%);
    }
  }
`;
